import React, { useState, useEffect, useRef, useContext } from "react";
import CreateObjectStep1 from './CreateObjectStep1';
import CreateObjectStep2 from './CreateObjectStep2';
import CreateObjectStep3 from './CreateObjectStep3';
import CreateObjectStep4 from './CreateObjectStep4';
import CreateObjectStep5 from './CreateObjectStep5';
import CreateObjectStep6 from './CreateObjectStep6';
import CreateObjectStep7 from './CreateObjectStep7';
import './object.css';
import StepFooter from "./StepFooter";
import { LanguageContext } from "../context/LanguageContext";


const defaultFormData = {
  name: '',
  object_type: '',
  environment: [],
  street: '',
  house_number: '',
  postal_code: '',
  city: '',
  country_id: '',
  region: '',
  licence_number: '',

  num_peoples: '',
  max_pets: "",
  num_bedrooms: 0,
  min_stay: '',
  arrival_days: [],

  amenities_bathroom: [],
  amenities_kitchen: [],
  amenities_bedroom: [],
  amenities_living_room: [],
  amenities_outdoor: [],
  amenities_environment: [],
  amenities_general: [],

  party_allowed: false,
  smoking_allowed: false,

  check_in: '',
  check_out: '',

  contact_method: '',
  website_url: '',
  contact_email: '',

  activity_nearby: '',
  description: '',
  title_image: '',
  additional_images: [],

  terms_accepted: false,
  terms_accepted_at: '',

  rooms: [],
};

const CreateObject = () => {
  const [currentStep, setCurrentStep] = useState(1);
  const [draftId, setDraftId] = useState(null);
  const [draftLoaded, setDraftLoaded] = useState(false);
  const API_URL = process.env.REACT_APP_API_URL;
  const step7SubmitRef = useRef(null);
  const [step7Complete, setStep7Complete] = useState(false);
  const [step7Loading, setStep7Loading] = useState(false);
  const { texts } = useContext(LanguageContext); 
  const stepLabels = texts.object.tabs; 


  const [stepErrors, setStepErrors] = useState({
    1: false,
    4: false,
    5: false,
    7: false,
  });

  const [validationErrors, setValidationErrors] = useState({});
  const [validationActive, setValidationActive] = useState(false);
  const [missingFields, setMissingFields] = useState({});

  const params = new URLSearchParams(window.location.search);
  const urlDraftId = params.get("draft_id");

  const [formData, setFormData] = useState(defaultFormData);

  const [isSmallScreen, setIsSmallScreen] = useState(
    typeof window !== 'undefined' ? window.innerWidth <= 480 : false
  );

  const [saveStatus, setSaveStatus] = useState("idle"); 
// idle | saving | saved

  // User-ID setzen + Draft erstellen oder bestehenden Draft verwenden
  useEffect(() => {
    const userId = localStorage.getItem('user_id');
    if (!userId) return;

    setFormData(prev => ({ ...prev, user_id: userId }));

    if (urlDraftId) {
      setDraftId(urlDraftId);
      return;
    }

    const createDraft = async () => {
      try {
        const res = await fetch(`${API_URL}/api/glamping/draft`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ user_id: userId })
        });

        const data = await res.json();
        setDraftId(data.draft_id);
      } catch (err) {
        console.error("❌ Draft konnte nicht erstellt werden:", err);
      }
    };

    createDraft();
  }, [urlDraftId, API_URL]);

  // Draft laden
  useEffect(() => {
    if (!draftId) return;

    const loadDraft = async () => {
      try {
        const res = await fetch(`${API_URL}/api/glamping/draft/${draftId}`);
        const draft = await res.json();

        if (draft.data) {
          // Draft normalisieren
          setFormData({
            ...defaultFormData,
            ...draft.data,
            arrival_days: Array.isArray(draft.data.arrival_days) ? draft.data.arrival_days : [],
            amenities_bathroom: Array.isArray(draft.data.amenities_bathroom) ? draft.data.amenities_bathroom : [],
            amenities_kitchen: Array.isArray(draft.data.amenities_kitchen) ? draft.data.amenities_kitchen : [],
            amenities_bedroom: Array.isArray(draft.data.amenities_bedroom) ? draft.data.amenities_bedroom : [],
            amenities_living_room: Array.isArray(draft.data.amenities_living_room) ? draft.data.amenities_living_room : [],
            amenities_outdoor: Array.isArray(draft.data.amenities_outdoor) ? draft.data.amenities_outdoor : [],
            amenities_environment: Array.isArray(draft.data.amenities_environment) ? draft.data.amenities_environment : [],
            amenities_general: Array.isArray(draft.data.amenities_general) ? draft.data.amenities_general : [],
            additional_images: Array.isArray(draft.data.additional_images) ? draft.data.additional_images : [],
            environment: Array.isArray(draft.data.environment) ? draft.data.environment : [],
          });
        }

        setDraftLoaded(true);
      } catch (err) {
        console.error("❌ Draft konnte nicht geladen werden:", err);
      }
    };

    loadDraft();
  }, [draftId, API_URL]);

  // Autosave bei jeder Änderung von formData
    useEffect(() => {
      if (!draftId || !draftLoaded) return;

      setSaveStatus("saving");

      const timeout = setTimeout(async () => {
        try {
          await fetch(`${API_URL}/api/glamping/draft/${draftId}`, {
            method: "PATCH",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ data: formData })
          });

          setSaveStatus("saved");

        } catch (err) {
          console.error("Fehler beim Speichern:", err);
        }
      }, 500);

      return () => clearTimeout(timeout);
    }, [formData, draftId, draftLoaded]);

  useEffect(() => {
    const onResize = () => setIsSmallScreen(window.innerWidth <= 480);
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  const maxStep = stepLabels.length;
  const handleNext = () => setCurrentStep(prev => Math.min(prev + 1, maxStep));
  const handleBack = () => setCurrentStep(prev => Math.max(prev - 1, 1));

return (
  <div className="create-object-wrapper">

    <div className="step-navigation-menu">

      {/* Left arrow */}
      {currentStep > 1 ? (
        <button
          type="button"
          className="step-edge-button step-edge-left"
          onClick={handleBack}
        >
          <svg width="16" height="16" viewBox="0 0 24 24">
            <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" />
          </svg>
        </button>
      ) : (
        <div className="step-edge-placeholder" />
      )}

      {/* Step Tabs */}
      {isSmallScreen ? (
        <select
          className="step-select"
          value={currentStep}
          onChange={(e) => setCurrentStep(Number(e.target.value))}
        >
          {stepLabels.map((label, i) => (
            <option key={i} value={i + 1}>
              {`${i + 1}. ${label}`}
            </option>
          ))}
        </select>
      ) : (
        stepLabels.map((label, index) => (
          <button
            key={index}
            className={`step-tab 
              ${currentStep === index + 1 ? 'active' : ''} 
              ${stepErrors[index + 1] ? 'error' : ''}`}
            onClick={() => setCurrentStep(index + 1)}
          >
            {label}
          </button>
        ))
      )}

      {/* Autosave Badge */}
      <div className="save-status-wrapper">
        {saveStatus === "saving" && (
          <span className="save-indicator saving">
            {texts.common.saving}
          </span>
        )}

        {saveStatus === "saved" && (
          <span className="save-indicator saved">
            {texts.common.saved}
          </span>
        )}
      </div>


      {/* Right arrow */}
      {currentStep < maxStep ? (
        <button
          type="button"
          className="step-edge-button step-edge-right"
          onClick={handleNext}
        >
          <svg width="16" height="16" viewBox="0 0 24 24">
            <path d="M9 6l6 6-6 6" stroke="currentColor" strokeWidth="2" />
          </svg>
        </button>
      ) : (
        <div className="step-edge-placeholder" />
      )}
    </div>

    <div className="step-content">

      {currentStep === 1 && (
        <CreateObjectStep1
          formData={formData}
          setFormData={setFormData}
          onNext={handleNext}
          missingFields={missingFields[1] || []}
          setMissingFields={setMissingFields}
          validationActive={validationActive}
        />
      )}

      {currentStep === 2 && (
        <CreateObjectStep2
          formData={formData}
          setFormData={setFormData}
          onNext={handleNext}
          onBack={handleBack}
          missingFields={missingFields[2] || []}
          setMissingFields={setMissingFields}
          validationActive={validationActive}
        />
      )}

      {currentStep === 3 && (
        <CreateObjectStep3
          formData={formData}
          setFormData={setFormData}
          onNext={handleNext}
          onBack={handleBack}
          missingFields={missingFields[3] || []}
          setMissingFields={setMissingFields}
          validationActive={validationActive}
        />
      )}

      {currentStep === 4 && (
        <CreateObjectStep4
          formData={formData}
          setFormData={setFormData}
          onNext={handleNext}
          onBack={handleBack}
          missingFields={missingFields[4] || []}
          setMissingFields={setMissingFields}
          validationActive={validationActive}
        />
      )}

      {currentStep === 5 && (
        <CreateObjectStep5
          formData={formData}
          setFormData={setFormData}
          onNext={handleNext}
          onBack={handleBack}
          missingFields={missingFields[5] || []}
          setMissingFields={setMissingFields}
          validationActive={validationActive}
        />
      )}

      {currentStep === 6 && (
        <CreateObjectStep6
          formData={formData}
          setFormData={setFormData}
          onNext={handleNext}
          onBack={handleBack}
          missingFields={missingFields[6] || []}
          setMissingFields={setMissingFields}
          validationActive={validationActive}
        />
      )}

      {currentStep === 7 && (
        <CreateObjectStep7
          formData={formData}
          setFormData={setFormData}
          onBack={handleBack}
          setCurrentStep={setCurrentStep}
          draftId={draftId}
          missingFields={missingFields}
          setMissingFields={setMissingFields}
          validationActive={validationActive}
          setValidationActive={setValidationActive}

          // ⭐ NEU
          registerSubmit={(fn) => (step7SubmitRef.current = fn)}
          onValidityChange={setStep7Complete}
          setLoading={setStep7Loading}
        />
      )}

    </div>

    {/* GLOBALER FOOTER HIER */}
    <StepFooter
      currentStep={currentStep}
      totalSteps={7}
      onBack={handleBack}
      onNext={() => {
        if (currentStep === 7) {
          if (step7SubmitRef.current) {
            step7SubmitRef.current();
          }
        } else {
          handleNext();
        }
      }}
      nextDisabled={currentStep === 7 ? !step7Complete || step7Loading : false}
      nextLabel={currentStep === 7 ? (step7Loading ? "Sende..." : "Absenden") : "Weiter"}
    />

  </div>
);

};

export default CreateObject;